// Core Tool System - Generic tool storage and registration
// Completely generic - no workspace/app-type logic

import { logger } from "../../../core/logger.js";

export class ToolSystem {
  constructor(options = {}) {
    // Tools storage
    // Map: className -> toolInstance
    // Also accessible via direct property: this.readFileTool
    this.tools = new Map();

    // Quadrad peer references (wired alongside AIToolSystem, AIInferenceSystem, BubbleUiSystem)
    this.aiToolSystem = null; // Reference to AIToolSystem
    this.aiInferenceSystem = null; // Reference to AIInferenceSystem
    this.bubbleUiSystem = null; // Reference to BubbleUiSystem
  }

  /**
   * Register a tool class
   * Creates an instance and stores it for access
   * @param {Function} ToolClass - Tool class constructor (not instance)
   * @returns {Object} The created tool instance
   */
  registerTool(ToolClass) {
    // Create instance (no parameters - BaseTool constructor is empty)
    const instance = new ToolClass();

    // Set toolSystem reference on instance
    instance.toolSystem = this;

    // Get class name (e.g., "ReadFileTool")
    const className = ToolClass.name;

    // Get instance tool name (e.g., "readFileTool")
    // Must be set by the tool instance
    const instanceName = instance.toolName;

    if (!instanceName) {
      throw new Error(
        `Tool class ${className} must explicitly set toolName property`
      );
    }

    // Check if tool is already registered
    if (this.checkToolRegistered(className)) {
      logger.warn(
        {
          tags: "tools|core|system|register",
          color1: "yellow",
        },
        `Tool '${className}' already registered, overwriting`
      );
    }

    // Store in map (key = class name)
    this.tools.set(className, instance);

    // Set direct property (key = instance name)
    this[instanceName] = instance;

    logger.log(
      {
        tags: "tools|core|system|register",
        color1: "lime",
      },
      `Registered tool: ${className} as '${instanceName}'`
    );

    return instance;
  }

  /**
   * Check if a tool is already registered
   * @param {string} className - Class name of the tool
   * @returns {boolean} True if tool is registered, false otherwise
   */
  checkToolRegistered(className) {
    return this.tools.has(className);
  }

  /**
   * Get a tool by class name
   * @param {string} className - Class name of the tool
   * @returns {Object} The tool instance
   */
  getToolByClassName(className) {
    return this.tools.get(className);
  }

  /**
   * Set the AI tool system reference
   * @param {AIToolSystem} aiToolSystem - The AI tool system instance
   */
  setAIToolSystem(aiToolSystem) {
    this.aiToolSystem = aiToolSystem;
  }

  /**
   * Set the AI inference system reference
   * @param {AIInferenceSystem} aiInferenceSystem - The AI inference system instance
   */
  setAIInferenceSystem(aiInferenceSystem) {
    this.aiInferenceSystem = aiInferenceSystem;
  }

  /**
   * Set the bubble UI system reference
   * @param {BubbleUiSystem} bubbleUiSystem - The bubble UI system instance
   */
  setBubbleUiSystem(bubbleUiSystem) {
    this.bubbleUiSystem = bubbleUiSystem;
  }
}
